import { useMemo, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";

import CurrentNumberHero from "./CurrentNumberHero";
import PreviousNumberTile from "./PreviousNumberTile";
import BingoBoard90 from "./BingoBoard90";
import StageLabel from "./StageLabel";
import HotelBrandCorner from "./HotelBrandCorner";
import StatusBadge from "./StatusBadge";
import { useDelayedReveal } from "./ball/useDelayedReveal";
import { useBingoSettingsStore } from "@/plugins/bingo/state/bingoSettingsStore";

/**
 * InGameView — the Projector layout during a live game.
 *
 * Layout:
 *   Top bar   : Hotel logo (left)   ·   Status + Stage (right)
 *   Middle    : Massive current number hero, previous tile bottom-left
 *   Bottom    : Full 1–90 board
 */
export default function InGameView({
  hotel,
  status,
  stage,
  called,
  manualMarks,
}) {
  const [current, previous] = useMemo(() => {
    const n = called.length;
    return [n > 0 ? called[n - 1].value : null, n > 1 ? called[n - 2].value : null];
  }, [called]);

  // Board cells stay dark until the reveal sequence promotes them.
  // Delay = ballAppear + ballSpin + ballOpen + revealPause + magicTrail.
  const timing = useBingoSettingsStore((s) => s.settings.revealTiming);
  const revealDelayMs =
    timing.ballAppearMs +
    timing.ballSpinMs +
    timing.ballOpenMs +
    timing.revealPauseMs +
    timing.magicTrailMs;
  const delayedRevealed = useDelayedReveal(called, revealDelayMs);
  // The ball-driven onRevealDone fires slightly earlier than the delay; we
  // union both so whichever wins first lights the cell.
  const [ballLit, setBallLit] = useState(new Set());
  const handleRevealDone = useCallback((v) => {
    setBallLit((prev) => {
      if (prev.has(v)) return prev;
      const next = new Set(prev);
      next.add(v);
      return next;
    });
  }, []);
  const revealedSet = useMemo(() => {
    if (ballLit.size === 0) return delayedRevealed;
    const merged = new Set(delayedRevealed);
    ballLit.forEach((v) => merged.add(v));
    return merged;
  }, [ballLit, delayedRevealed]);

  // If the game resets, clear ball-lit set so next round starts clean.
  useEffect(() => {
    if (called.length === 0) setBallLit(new Set());
  }, [called.length]);

  return (
    <div
      data-testid="projector-in-game"
      className="fixed inset-0 w-screen h-screen overflow-hidden flex flex-col"
      style={{ background: "var(--ic-bg-darkest)" }}
    >
      {/* Subtle ambient background */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(1400px 800px at 50% 30%, var(--ic-primary-glow), transparent 65%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Top bar */}
      <header className="relative z-10 flex items-start justify-between px-12 pt-8">
        <HotelBrandCorner hotel={hotel} />
        <div className="flex flex-col items-end gap-3">
          <StatusBadge status={status} />
          <StageLabel stage={stage} />
        </div>
      </header>

      {/* Middle — hero + previous */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 flex-1 min-h-0 flex items-center justify-center"
      >
        <div className="relative w-full h-full flex items-center justify-center">
          <CurrentNumberHero value={current} onRevealDone={handleRevealDone} />
          <div className="absolute left-[6vw] bottom-[4vh]">
            <PreviousNumberTile value={previous} />
          </div>
        </div>
      </motion.section>

      {/* Bottom — board */}
      <section
        data-testid="projector-board-section"
        className="relative z-10 px-8 pb-8"
        style={{ height: "35vh" }}
      >
        <BingoBoard90
          called={called}
          manualMarks={manualMarks}
          revealedSet={revealedSet}
          pulseMs={timing.boardPulseMs}
        />
      </section>
    </div>
  );
}
